import { createSlice, WritableDraft } from "@reduxjs/toolkit";
import { resetStore } from "../slices/store.js";
import { Revision, StoreRevisions } from "../types/index.js";
import { fetch, formatDateTime } from "../utils/utils.js";
import { showAlert } from "./alerts.js";
import { createAppAsyncThunk } from "./utils.js";

const sliceName = "revisions";

const initialState: StoreRevisions = { allIds: [], byId: {} };

export const resetRevision = createAppAsyncThunk(
  `${sliceName}/resetRevision`,
  async (
    { siteId, revisionId }: { siteId: string; revisionId: string },
    { dispatch, getState },
  ) => {
    const revision = getState().revisions.byId[revisionId];
    if (!revision) {
      throw Error("Site revision not found");
    }

    const { publishedAt } = revision;
    const timestamp = formatDateTime(publishedAt);
    try {
      await fetch({ dispatch }).post(
        `sites/${siteId}/revisions/${revisionId}/reset`,
      );

      // needs to stay separate action dispatch instead of extraReducers in store as we check on that action
      dispatch(resetStore());

      dispatch(
        showAlert(
          `Zurücksetzen auf den Stand vom ${timestamp} erfolgreich!`,
          "success",
        ),
      );
    } catch (error) {
      dispatch(
        showAlert(
          `Beim Zurücksetzen auf den Stand vom ${timestamp} ist ein Fehler aufgetreten!`,
        ),
      );
      throw error;
    }
  },
  {
    dispatchConditionRejection: true,
    condition: ({ revisionId }, { getState }) =>
      !!getState().revisions.byId[revisionId],
  },
);

export const getRevisions = createAppAsyncThunk(
  `${sliceName}/getRevisions`,
  async (siteId: string, { dispatch }) => {
    try {
      const { data } = await fetch({ dispatch }).get<Revision[]>(
        `sites/${siteId}/revisions`,
      );
      return data;
    } catch (error) {
      dispatch(
        showAlert(
          "Beim Laden der Veröffentlichungen ist ein Fehler aufgetreten!",
        ),
      );
      throw error;
    }
  },
);

export const patchRevision = createAppAsyncThunk(
  `${sliceName}/patchRevision`,
  async (
    {
      siteId,
      revisionId,
      isFavorite,
    }: { siteId: string; revisionId: string; isFavorite: boolean },
    { dispatch, getState },
  ) => {
    const revision = getState().revisions.byId[revisionId];
    if (!revision) {
      throw Error("Site revision not found");
    }

    const { publishedAt } = revision;
    const timestamp = formatDateTime(publishedAt);
    try {
      await fetch({ dispatch }).patch(
        `sites/${siteId}/revisions/${revisionId}`,
        { isFavorite },
      );
    } catch (error) {
      dispatch(
        showAlert(
          `Beim Favorisieren des Stands vom ${timestamp} ist ein Fehler aufgetreten!`,
        ),
      );
      throw error;
    }
  },
  {
    dispatchConditionRejection: true,
    condition: ({ revisionId }, { getState }) =>
      !!getState().revisions.byId[revisionId],
  },
);

const setFavorite = (
  state: WritableDraft<StoreRevisions>,
  revisionId: string,
  isFavorite: boolean,
) => {
  const revision = state.byId[revisionId];
  if (!revision) throw Error("Site revision not found");
  revision.isFavorite = isFavorite;
};

const slice = createSlice({
  name: sliceName,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getRevisions.fulfilled, (_state, action) =>
      action.payload.reduce<StoreRevisions>(
        ({ allIds, byId }, revision) => ({
          allIds: [...allIds, revision.id],
          byId: { ...byId, [revision.id]: revision },
        }),
        initialState,
      ),
    );

    builder.addCase(patchRevision.pending, (state, action) => {
      const { revisionId, isFavorite } = action.meta.arg;
      setFavorite(state, revisionId, isFavorite);
    });

    builder.addCase(patchRevision.rejected, (state, action) => {
      if (action.meta.condition) {
        return;
      }

      const { revisionId, isFavorite } = action.meta.arg;
      // Undo the action by setting the inverse isFavorite status since the optimistic update in pending already set it to the new value
      setFavorite(state, revisionId, !isFavorite);
    });
  },
});

export default slice.reducer;
